import type { CheckoutConfig, Order } from './api';

/**
 * The SaturnShift hosted-checkout handoff.
 *
 * ---------------------------------------------------------------------------
 * THE BROWSER IS A COURIER, NOT AN AUTHORITY.
 *
 * The widget takes the amount as a parameter, which means this file hands SaturnShift a
 * number. That number comes from the ORDER THE SERVER JUST CREATED and from nowhere
 * else — never from form state, never recomputed, never adjusted. Once `POST /api/orders`
 * returns, `order.pricing.totalCents` is the only amount that exists.
 *
 * Because the value passes through a browser it cannot be trusted by MM either. That is
 * why the webhook (slice 4) enforces `gross >= total_cents` server-side: this handoff is
 * convenience, and the server-side check is the control.
 *
 * ---------------------------------------------------------------------------
 * NOTHING HERE CONFIRMS A PAYMENT.
 *
 * `checkout()` navigates away. It returns no transaction id, exposes no callback, and
 * reports no success — the handoff is explicit that the widget hands nothing back. A
 * customer arriving at the return URL proves only that a browser followed a redirect.
 * Order state moves on a verified webhook or an authenticated reconciliation, and on
 * nothing else.
 */

/** The subset of the widget surface MM uses. It exposes nothing else worth typing. */
interface SaturnShiftGlobal {
  checkout: (config: Record<string, unknown>, selector?: string) => void;
}

declare global {
  interface Window {
    SaturnShift?: SaturnShiftGlobal;
  }
}

/** Marks our injected tag so a re-render finds it instead of adding another. */
const SCRIPT_ID = 'saturnshift-checkout-js';

/** How long to wait for `checkout.js` before giving up and letting the customer retry. */
const SCRIPT_TIMEOUT_MS = 15_000;

/**
 * How long to wait for the widget to navigate away before treating it as failed.
 *
 * Generous, because it covers a real round trip to the payment provider. What it must
 * not be is absent -- see the note on `startCheckout`.
 */
const WIDGET_NAVIGATION_TIMEOUT_MS = 20_000;

/**
 * THE LOAD CURRENTLY IN FLIGHT, IF ANY.
 *
 * Module-level so a second call joins the first rather than trying to re-observe a
 * <script> tag it did not create. Cleared on failure so a retry gets a genuine second
 * attempt.
 */
let inFlight: Promise<SaturnShiftGlobal> | null = null;

export class CheckoutUnavailableError extends Error {
  constructor(message = 'Could not reach the payment provider.') {
    super(message);
    this.name = 'CheckoutUnavailableError';
  }
}

/**
 * Convert the authoritative total to the decimal USD the widget expects.
 *
 * The widget takes decimal dollars (`49.99`), not minor units — the one unit mismatch in
 * this integration, and the one most likely to send a customer to a checkout for a
 * hundredth of the price.
 *
 * `toFixed(2)` before `Number` is deliberate. Plain `cents / 100` is very nearly always
 * right, but rounding the quotient to exactly two decimals first makes the intent
 * explicit and forecloses any binary-representation artefact reaching a payment
 * provider as, say, `413.10000000000002`.
 *
 *   45900 → 459      41310 → 413.1 (i.e. $413.10)      1 → 0.01
 */
export function centsToDecimalAmount(cents: number): number {
  if (!Number.isFinite(cents) || !Number.isInteger(cents) || cents < 0) {
    throw new CheckoutUnavailableError('The order total is not a usable amount.');
  }
  return Number((cents / 100).toFixed(2));
}

/**
 * Load `checkout.js` from the API origin the server named, exactly once.
 *
 * IDEMPOTENT IN THREE WAYS, because this is called on mount, on retry, and on re-render:
 *   1. an already-present `window.SaturnShift` resolves immediately, loading nothing;
 *   2. an existing tag is awaited rather than duplicated;
 *   3. a failed tag is removed, so a retry gets a genuine second attempt rather than
 *      re-awaiting a script that will never load.
 *
 * It does not retry by itself. A loop against an unreachable origin would hammer it and
 * hide the failure; the customer is shown an error and chooses to retry.
 */
export function loadCheckoutScript(apiOrigin: string): Promise<SaturnShiftGlobal> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(new CheckoutUnavailableError());
  }

  if (window.SaturnShift) {
    return Promise.resolve(window.SaturnShift);
  }

  if (!apiOrigin) {
    // The server did not supply an origin — SATURNSHIFT_API_BASE_URL is unset. Say so as
    // a checkout failure rather than requesting `undefined/checkout.js`.
    return Promise.reject(new CheckoutUnavailableError());
  }

  /*
   * A LOAD ALREADY UNDER WAY IS JOINED, NOT RE-OBSERVED.
   *
   * This replaces reading the DOM for an existing tag and attaching listeners to it.
   * That was a permanent hang: if the tag had ALREADY loaded, its `load` event had
   * fired long before, the new listeners never ran, and the returned promise never
   * settled. `await` on it hung forever, which is precisely what pinned the button at
   * "Opening secure checkout…" with no request and no error.
   */
  if (inFlight) {
    return inFlight;
  }

  /*
   * A TAG WITH NO PROMISE BEHIND IT IS UNUSABLE, SO IT IS REPLACED.
   *
   * We know `window.SaturnShift` is absent, so either that tag is still loading with
   * nobody watching it, or it finished without defining the global. Neither can be
   * awaited. Removing it and injecting a fresh one guarantees a `load` or `error` we
   * actually hear.
   */
  document.getElementById(SCRIPT_ID)?.remove();

  const script = document.createElement('script');

  const settled = new Promise<SaturnShiftGlobal>((resolve, reject) => {
    /*
     * THE BACKSTOP. Every path out of this promise settles, including the ones nobody
     * anticipated: a tag that neither loads nor errors, a proxy that holds the
     * connection open, a blocked request. A checkout that cannot open must fail
     * visibly, because the customer can retry a failure and can do nothing at all with
     * a spinner.
     */
    const timer = setTimeout(() => {
      script.remove();
      inFlight = null;
      reject(new CheckoutUnavailableError());
    }, SCRIPT_TIMEOUT_MS);

    script.addEventListener('load', () => {
      clearTimeout(timer);
      if (window.SaturnShift) resolve(window.SaturnShift);
      else {
        // Loaded, but the global is absent: the origin served something that is not the
        // widget. Treated as unavailable rather than assumed usable.
        inFlight = null;
        reject(new CheckoutUnavailableError());
      }
    });

    script.addEventListener('error', () => {
      clearTimeout(timer);
      script.remove();
      // Cleared so a retry genuinely re-attempts rather than re-awaiting a dead load.
      inFlight = null;
      reject(new CheckoutUnavailableError());
    });
  });

  script.id = SCRIPT_ID;
  script.src = `${apiOrigin.replace(/\/+$/, '')}/checkout.js`;
  script.async = true;
  // 7. a script really is being appended -- expect a checkout.js request right after.
  document.head.appendChild(script);

  inFlight = settled;
  return settled;
}

/** Test seam: forget any cached load, as a fresh document would. */
export function resetCheckoutScriptForTesting(): void {
  inFlight = null;
}
/**
 * Metered Mining's own page background, for SaturnShift's hosted checkout to match.
 *
 * ===========================================================================
 * READ FROM THE LIVE TOKEN, NOT COPIED INTO THIS FILE.
 *
 * `--mm-bg` in styles/index.css is the authority and already has two values -- a warm
 * off-white for the light theme and a near-black for dark. Writing either of them here
 * as a literal would be a second copy of a design token that is guaranteed to drift, and
 * it would hand a dark-theme customer a white checkout page, which is the exact seam
 * this feature exists to remove.
 *
 * Reading the computed value costs one call, needs no theme architecture, and is correct
 * in both themes for free -- the customer leaves a page of one colour and arrives on one
 * of the same colour.
 *
 * ===========================================================================
 * ONLY A 6-DIGIT HEX LEAVES THIS FUNCTION.
 *
 * That is the whole contract: exactly `#RRGGBB`, case-insensitive. Anything else --
 * a 3-digit shorthand, an `rgb()`, a `color-mix()`, an empty string because no
 * stylesheet is attached -- returns null and the field is omitted rather than sent as
 * something SaturnShift would have to discard. They ignore an invalid value silently, so
 * sending one would fail invisibly; sending nothing at least fails the same way on
 * purpose.
 *
 * PRESENTATION ONLY. It reaches the widget beside title and description, and it changes
 * the hosted page's background and nothing else -- not the amount, the currency, the
 * rails, the redirect, or anything that decides whether the order gets paid.
 */
const HEX_COLOR = /^#[0-9a-f]{6}$/i;

export function pageBackgroundColor(): string | null {
  if (typeof document === 'undefined' || typeof getComputedStyle !== 'function') return null;

  try {
    const value = getComputedStyle(document.documentElement)
      .getPropertyValue('--mm-bg')
      .trim();

    return HEX_COLOR.test(value) ? value : null;
  } catch {
    // A computed-style read should not be able to stop a customer paying.
    return null;
  }
}

/**
 * What `startCheckout` needs: a persisted order, and the server's checkout block for it.
 *
 * DELIBERATELY STRUCTURAL, NOT `CreatedOrder`. The same handoff serves two entry points —
 * a customer paying immediately after configuring, and one returning days later to an
 * order they left unpaid — and `CreatedOrder` satisfies this shape as-is. Two entry
 * points, ONE implementation: a second copy of this function is how the two paths would
 * quietly start passing different amounts or a different idempotency key.
 */
export interface CheckoutHandoff {
  order: Order;
  checkout: CheckoutConfig;
}

/**
 * Hand the customer to SaturnShift's hosted checkout for an already-created order.
 *
 * EVERY VALUE COMES FROM THE SERVER'S OWN RECORD OF THAT ORDER — the create-order
 * response, or the authenticated account response for one that was never paid. Never from
 * form state, and never recomputed. `externalReference` and `idempotencyKey` are both the
 * MM order number, as the handoff specifies: one checkout per order, and a stable
 * correlation key for the webhook and for reconciliation.
 *
 * THAT PAIRING IS WHAT MAKES BOTH RETRY AND RESUME SAFE. Calling this again for the same
 * order — a second later after the widget failed to open, or a week later from the order
 * page — presents the SAME key for the SAME amount. It is why neither path may ever fall
 * back to `POST /api/orders`: the order exists, and a capped discount may already be
 * holding a reservation against it.
 */
export async function startCheckout(handoff: CheckoutHandoff): Promise<void> {
  const { order, checkout } = handoff;

  if (!checkout?.publicKey || !checkout?.apiOrigin) {
    throw new CheckoutUnavailableError('Payments are not configured.');
  }


  const saturnShift = await loadCheckoutScript(checkout.apiOrigin);


  const tier = order.configuration?.tier;
  const hashrate = tier?.displayHashrate ?? `${order.configuration?.hashrateTh ?? ''} TH/s`;

  // Read at handoff, so it reflects the theme the customer is actually looking at.
  const backgroundColor = pageBackgroundColor();

  saturnShift.checkout({
    publicKey: checkout.publicKey,
    // The server's figure, converted to the widget's unit. Nothing else.
    amount: centsToDecimalAmount(order.pricing.totalCents),
    currency: order.pricing.currency,
    // Both the MM order number: one checkout per order, one correlation key.
    idempotencyKey: order.orderNumber,
    externalReference: order.orderNumber,
    redirectUrl: checkout.redirectUrl,
    /*
     * PREFILL FOR THE RECEIPT-EMAIL FIELD, from the server's block — never from the
     * form the customer just filled in. The server publishes the email it already
     * resolved for this order, so the prefill cannot disagree with the order it belongs
     * to.
     *
     * SPREAD RATHER THAN SET, so an order without one passes no key at all. `undefined`
     * is neither an address nor an absent field, and the widget should not have to
     * decide which we meant.
     */
    ...(checkout.customerEmail ? { customerEmail: checkout.customerEmail } : {}),
    // Described from the order that exists, not from form state.
    title: 'Metered Mining hashrate rental',
    description: `${hashrate} for ${order.configuration?.termDays ?? ''} days`,
    openInNewTab: false,
    allowCard: true,
    allowBank: true,
    allowCrypto: true,
    /*
      THE HOSTED PAGE'S BACKGROUND, so the handoff does not flash a different colour.
      Sent fresh on every call -- SaturnShift keeps the latest value for an open
      idempotency key, and this must not rely on an earlier one having stuck. Omitted
      entirely when the token cannot be read as a 6-digit hex; the widget then uses its
      own default, which is what happens today.
    */
    ...(backgroundColor ? { backgroundColor } : {}),
    apiUrl: checkout.apiOrigin,
  });

  // 8/9. the widget was invoked. A POST to /api/payments/public/payment-intent should
  // follow immediately; if it does not, the widget itself is where this stops.

  /*
   * =========================================================================
   * THE WIDGET TELLS US NOTHING, SO SILENCE IS TREATED AS FAILURE.
   *
   * On success the widget sets `window.location`, this document unloads, and nothing
   * below ever runs. That is the ONLY success signal available.
   *
   * On failure it catches its own error, writes it to the console, and returns
   * normally — it reports nothing to the caller and renders nothing itself when it was
   * invoked without a button. Without this wait, `startCheckout` would resolve as
   * though it had succeeded and the caller would sit on "Opening secure checkout…"
   * forever, with no error and no way back.
   *
   * So: still here after the timeout means it did not navigate. Rejecting hands the
   * caller its existing retry path. The order is untouched either way, and a retry
   * presents the same order number, the same key and the same amount.
   */

  await new Promise<never>((_resolve, reject) => {
    setTimeout(
      () => reject(new CheckoutUnavailableError()),
      WIDGET_NAVIGATION_TIMEOUT_MS,
    );
  });
}
